import { AuthContext } from 'context';
import { useContext, useState } from 'react';
import changeLogService from 'services/change-log-service';
import inventoryManagementService from 'services/inventory-items-service';
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";

// MUI and Material Dashboard Components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Icon from "@mui/material/Icon";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MDBox from "components/MDBox";
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";


export default function AjustStockDialog({
    handleClose,
    open,
    id,
    initial_qty
}) {
    const [quantity, setQuantity] = useState(0);
    const [comment, setComment] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [errors, setErrors] = useState({});
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const authContext = useContext(AuthContext);
    const ability = useAbility(AbilityContext);

    // Validate form fields
    const checkErrors = () => {
        let tempErrors = {};

        if (quantity <= 0) {
            tempErrors.quantity = "Quantity must be greater than zero.";
        } if (tabValue === 2 && comment.trim() === '') {
            tempErrors.comment = "Comment is required for reservations.";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    }
    // Handle Close Snackbar
    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
        setErrors({});
    }

    // Handle Set Tab Value
    const handleSetTabValue = (event, newValue) => {
        if (newValue !== null) {
            setTabValue(newValue);
        }
    };

    // Handle Save button click
    const onClickSave = () => {
        if (tabValue === 0) {
            handleIncrease();
        } else if (tabValue === 1) {
            handleDecrease();
        } else if (tabValue === 2) {
            handleReserve();
        }
    }

    // Handle stock increase
    const handleIncrease = () => {
        const newQty = parseInt(initial_qty) + parseInt(quantity);
        submit(newQty);
    }

    // Handle stock decrease
    const handleDecrease = () => {
        const newQty = parseInt(initial_qty) - parseInt(quantity);
        submit(newQty);
    }

    // Handle & Submit Reservation
    const handleReserve = async () => {
        try {
            setErrors({});
            if (!checkErrors()) {
                return;
            }
            const qty = parseInt(quantity);
            const reservationPayload = {
                data: {
                    attributes: {
                        qty: qty,
                        comment: comment,
                        status: 'allocated',
                        inventoryItem: id,
                        user: await authContext.getCurrentUser()
                    }
                }
            }

            const res = await inventoryManagementService.createReservation(reservationPayload);
            console.log("res status", res.status);

            setQuantity(0);
            setComment('');
            handleClose();
        } catch (error) {
            console.error("Error creating reservation:", error);
            setErrors({ submit: error.error || 'Failed to create reservation. Please try again.' });
            setOpenSnackbar(true);
        }
    }

    // Submit stock adjustment
    const submit = async (quantity) => {
        try {
            setErrors({});
            if (!checkErrors()) {
                return;
            }
            const changeLog = {
                data: {
                    attributes: {
                        inventoryItem: id,
                        change: {
                            type: (tabValue === 0) ? "Stock Increase" : "Stock decrease",
                            description: `Stock level adjusted by ${quantity}`,
                            date: new Date(),
                            user: await authContext.getCurrentUser()
                        }
                    }
                }
            }
            await changeLogService.addChange(changeLog);
            const payload = {
                data: {
                    type: "inventoryItem",
                    id: id,
                    attributes: {
                        newQty: quantity
                    }
                }
            }
            inventoryManagementService.adjustStock(id, payload)
                .then(() => {
                    handleClose();
                    setQuantity(0);
                })
                .catch((err) => {
                    console.error(err);
                });
        } catch (error) {
            console.error("Error adjusting stock:", error);
        }
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
            >
                {/* Snackbar for server-side errors */}
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity="error"
                        sx={{ width: '100%', alignItems: 'center' }}
                    >
                        {errors.submit}
                    </Alert>
                </Snackbar>

                {/* Dialog Title */}
                <DialogTitle>Adjust Stock Level</DialogTitle>

                <DialogContent>
                    <MDBox>

                        {/* Toggle Button Group for Adjustment Type */}
                        <ToggleButtonGroup
                            color="info"
                            value={tabValue}
                            exclusive
                            onChange={handleSetTabValue}
                            aria-label="Adjustment Type"
                            fullWidth
                        >
                            <ToggleButton value={0}>
                                <Icon sx={{ mr: 1 }}>trending_up</Icon>
                                Increase
                            </ToggleButton>

                            <ToggleButton value={1}>
                                <Icon sx={{ mr: 1 }}>trending_down</Icon>
                                Decrease
                            </ToggleButton>

                            {ability.can("create", "reservation") && (
                                <ToggleButton value={2}>
                                    <Icon sx={{ mr: 1 }}>bookmark_added</Icon>
                                    Reserve
                                </ToggleButton>
                            )}
                        </ToggleButtonGroup>

                        {/* Quantity Input Field */}
                        <MDBox mt={2}>
                            <MDInput
                                label="Quantity"
                                type="number"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                error={errors.quantity}
                                helperText={errors.quantity}
                                fullWidth
                            />
                        </MDBox>

                        {/* Comment Input Field for Reservation */}
                        {tabValue === 2 && (
                            <MDBox mt={1}>
                                <MDInput
                                    label="Comment"
                                    type="text"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    error={errors.comment}
                                    helperText={errors.comment}
                                    fullWidth
                                />
                            </MDBox>
                        )}
                    </MDBox>
                </DialogContent>

                {/* Dialog Actions */}
                <DialogActions fullWidth>
                    <MDButton
                        onClick={handleClose}
                        color="secondary"
                        variant="outlined"
                        size="small"
                        fullWidth
                    >
                        Cancel
                    </MDButton>
                    <MDButton
                        onClick={onClickSave}
                        color="info"
                        variant="outlined"
                        size="small"
                        fullWidth
                    >
                        Save
                    </MDButton>
                </DialogActions>
            </Dialog>
        </>
    )
}